import React, { useRef } from 'react'

import MoneyField from 'src/components/MoneyField'

import SandwichField from './SandwichField'
import { supportsFancySelect } from './supportsFancySelect'

const PRESET_LAYOUT = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 @roomy:grid-cols-4',
}

const Amount = ({
  i18n, donation, currencies, errors, multiOrg, multiProject,
}) => {
  const { state, currency, presets, setAmount, setCurrency } = donation
  const amountError = errors?.amount

  // Same gating as DonationLine: no impact line on a fundraiser that
  // splits across orgs or offers a choice of projects, since the
  // organizer's one impact statement can't describe either. Would need
  // impact metrics attached to individual orgs / projects to compose
  // meaningfully. Both flags are about how the fundraiser is set up, not
  // what the donor has picked so far — a sole designation is applied
  // silently, so its statement holds and the line renders.
  const activePreset = !multiOrg && !multiProject
    && (presets || []).find(p => p.amount === state.amount)
  const presetOptions = (presets || []).slice(0, 6)
  // With 5 presets, render an "Other" radio in the trailing 6th cell so the
  // 3-col grid stays balanced and the user has an explicit way to opt into
  // a custom amount.
  const showOther = presetOptions.length === 5
  const presetLayout = PRESET_LAYOUT[presetOptions.length] || PRESET_LAYOUT[3]
  const amountRef = useRef()
  const otherSelected = !presetOptions.some(p => p.amount === state.amount)

  const handleAmountChange = e => {
    const value = e.target.value
    const index = (presets || []).findIndex(p => p.amount === value)
    setAmount(value, index)
  }

  // "Other" clears the amount and moves focus to the input. Triggered by
  // both mouse click and keyboard arrow-nav (radio onChange fires for
  // either) — the focus jump is intentional: the user's next step is
  // entering their own amount, so handing them the input is the natural
  // flow even if it interrupts radio-group keyboard navigation.
  const handleOther = () => {
    setAmount('')
    amountRef.current?.focus()
  }

  // Build inline prefix/suffix from i18n.currency.format (e.g. "%u%n",
  // "%n %u"). %c (currency code) is stripped from the format and instead
  // appended to the suffix only when there's a single currency — when
  // multiple currencies are configured the iso code lives in the selector.
  const format = (i18n.currency?.format || '%u%n').replace('%c', '').trim()
  const [ prefixTemplate = '', suffixTemplate = '' ] = format.split('%n')
  const prefixText = prefixTemplate.replace('%u', currency.symbol || '')
  const suffixText = [
    suffixTemplate.replace('%u', currency.symbol || ''),
    currencies.length > 1 ? null : <small key="iso_code">{currency.iso_code}</small>,
  ].filter(Boolean)
  // Space-separate the two parts when both render. .join(' ') would
  // coerce the <small> React element to "[object Object]", so insert
  // the separator into the array and let React render the pieces.
  if (suffixText.length == 2) suffixText.splice(1, 0, ' ')

  return (
    <div className="space-y-3">
      {presetOptions.length > 0 && (
        <fieldset>
          <legend className="sr-only">{i18n.t('suggested_amounts')}</legend>
          <div className={`grid gap-2 ${presetLayout}`}>
            {presetOptions.map(preset => (
              <label key={preset.amount} className="btn btn-lg btn-radio text-xl">
                <input
                  type="radio"
                  name="amount-preset"
                  value={preset.amount}
                  checked={state.amount === preset.amount}
                  onChange={handleAmountChange}
                  className="sr-only"
                />
                {i18n.formatCurrency(preset.amount, currency)}
              </label>
            ))}
            {showOther && (
              <label className="btn btn-lg btn-radio text-xl">
                <input
                  type="radio"
                  name="amount-preset"
                  value=""
                  checked={otherSelected}
                  onChange={handleOther}
                  className="sr-only"
                />
                {i18n.t('other')}
              </label>
            )}
          </div>
        </fieldset>
      )}

      <div>
        <label htmlFor="amount" className="sr-only">{i18n.t('amount')}</label>
        <div className="input-group">
          <div className="input-group-cell flex-1 min-w-0">
            <SandwichField
              ref={amountRef}
              type={MoneyField}
              id="amount"
              exponent={currency.exponent}
              decimal={i18n.currency?.separator}
              value={state.amount}
              onChange={e => setAmount(e.target.value)}
              aria-invalid={amountError ? 'true' : undefined}
              placeholder={i18n.t('amount')}
              className="form-control form-control-lg input-group-control"
              prefix={prefixText && <span className="text-gray-500"> {prefixText} </span>}
              suffix={suffixText && <span className="text-gray-500"> {suffixText} </span>}
            />
          </div>
          {currencies.length > 1 && (
            <div className="input-group-cell">
              <label htmlFor="currency" className="sr-only">{i18n.t('currency')}</label>
              <select
                id="currency"
                value={state.currency}
                onChange={e => setCurrency(e.target.value)}
                className="form-select form-select-sm input-group-island w-auto field-sizing-content text-end custom-select"
              >
                <button><selectedcontent /></button>
                {currencies.map(c => (
                  <option key={c.id} value={c.id} title={c.name}>
                    {c.iso_code}
                    {supportsFancySelect && <span className="hidden"> — {c.name}</span>}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {amountError && (
          <p className="mt-1 text-sm text-danger">{amountError}</p>
        )}
      </div>

      {activePreset?.impact && (
        <p className="rounded-lg border border-brand-500 bg-brand-50 p-3 @roomy:p-4 flex items-center gap-3">
          {activePreset.emoji && (
            <span className="text-xl" aria-hidden="true">{activePreset.emoji}</span>
          )}
          <span className="font-semibold">
            {i18n.formatCurrency(state.amount, currency)}
            {' '}
            {activePreset.impact}
          </span>
        </p>
      )}
    </div>
  )
}

export default Amount
